import { Head, router } from "@inertiajs/react"
import { Check, ListFilter, MessageCircle } from "lucide-react"
import { useState } from "react"

import {
  ContactDrawer,
  type SelectedConversation,
} from "@/components/contact-drawer"
import { type Channel, channelIcons } from "@/components/conversation-channel"
import { PageHeading, PageLayout } from "@/components/page-layout"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Empty } from "@/components/ui/empty"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import { homePath } from "@/routes"

// IMP-520: Home is the assistant's activity feed — a greeting plus one
// day-grouped list of conversations across every job, regardless of which
// model recorded them. Job merchandising lives on the Jobs page.

interface FeedRow {
  id: string
  channel: Channel
  // The person's name when one exists, else their formatted number; null
  // reads as an anonymous visitor.
  person: string | null
  summary: string | null
  occurred_at: string
  day_key: string
  day: string
  time: string
}

interface Props {
  first_name: string | null
  feed: FeedRow[]
  // IMP-521: the contact drawer's payload for ?conversation=<uid>.
  selected_conversation: SelectedConversation | null
}

type FilterKey = "all" | Channel

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All conversations" },
  { key: "inbound", label: "Inbound calls" },
  { key: "outbound", label: "Outbound calls" },
  { key: "chat", label: "Chats" },
]

// "Inbound call from Marcus Reyes", "Outbound call to Dana Whitfield",
// "Inbound chat from Anonymous visitor" — the row's title reads as one
// sentence naming direction and person.
const SENTENCE_LEAD: Record<Channel, string> = {
  inbound: "Inbound call from",
  outbound: "Outbound call to",
  chat: "Inbound chat from",
}

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

// A feed row: a neutral icon tile (direction reads from the glyph's shape,
// not a hue), the whole sentence as the bold title with the time opposite,
// and the summary clamped to one line beneath.
function ActivityRow({ row, onOpen }: { row: FeedRow; onOpen: () => void }) {
  const Icon = channelIcons[row.channel]
  return (
    <button
      type="button"
      onClick={onOpen}
      className="hover:bg-muted/40 -mx-3 flex w-full cursor-pointer items-start gap-4 rounded-xl px-3 py-3 text-left transition-colors"
    >
      <div className="bg-muted flex size-11 shrink-0 items-center justify-center rounded-xl">
        <Icon className="text-muted-foreground size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-4">
          <p className="truncate text-sm font-semibold">
            {SENTENCE_LEAD[row.channel]} {row.person ?? "Anonymous visitor"}
          </p>
          <span className="text-muted-foreground ml-auto shrink-0 text-sm">
            {row.time}
          </span>
        </div>
        {row.summary && (
          <p className="text-muted-foreground mt-1 line-clamp-1 text-sm">
            {row.summary}
          </p>
        )}
      </div>
    </button>
  )
}

export default function Home({
  first_name,
  feed,
  selected_conversation,
}: Props) {
  const [filter, setFilter] = useState<FilterKey>("all")
  // Closing is optimistic — the sheet animates out immediately while the
  // partial visit clears the prop (and the ?conversation param) behind it.
  const [closing, setClosing] = useState(false)
  const drawerOpen = selected_conversation != null && !closing

  function openConversation(id: string) {
    setClosing(false)
    router.visit(homePath({ conversation: id }), {
      only: ["selected_conversation"],
      preserveState: true,
      preserveScroll: true,
    })
  }

  function closeDrawer() {
    setClosing(true)
    router.visit(homePath(), {
      only: ["selected_conversation"],
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setClosing(false),
    })
  }

  const rows =
    filter === "all" ? feed : feed.filter((row) => row.channel === filter)

  // Group by real dates, most recent first; days with no activity simply
  // don't appear, so headings may be non-consecutive.
  const groups: { key: string; day: string; rows: FeedRow[] }[] = []
  for (const row of rows) {
    const last = groups[groups.length - 1]
    if (last && last.key === row.day_key) {
      last.rows.push(row)
    } else {
      groups.push({ key: row.day_key, day: row.day, rows: [row] })
    }
  }

  return (
    <AppLayout>
      <Head title="Home" />

      {/* Narrower than PageLayout's max-w-6xl default: the feed is a single
          reading column, and summaries set the measure. */}
      <PageLayout className="max-w-4xl">
        {/* Home is greeting plus feed — nothing else. */}
        <PageHeading>
          {first_name ? `${greeting()}, ${first_name}` : greeting()}
        </PageHeading>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Activity</h2>
            {/* The filter rides the Activity heading as one control. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "size-8",
                    filter === "all"
                      ? "text-muted-foreground"
                      : "text-foreground",
                  )}
                  aria-label="Filter by channel"
                >
                  <ListFilter className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {FILTERS.map(({ key, label }) => (
                  <DropdownMenuItem key={key} onClick={() => setFilter(key)}>
                    <span className="flex-1">{label}</span>
                    {filter === key && <Check className="size-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div>
            {groups.length === 0 ? (
              <div className="px-4 py-12">
                <Empty
                  icon={<MessageCircle className="size-5" />}
                  title="No conversations yet"
                  description="Calls and chats your assistant handles will appear here."
                />
              </div>
            ) : (
              // An open list, not a boxed one: plain date headings over
              // borderless rows, groups separated by air.
              groups.map((group, index) => (
                <div key={group.key} className={cn(index > 0 && "mt-8")}>
                  <p className="text-muted-foreground mb-2 text-sm">
                    {group.day}
                  </p>
                  <div className="space-y-1">
                    {group.rows.map((row) => (
                      <ActivityRow
                        key={row.id}
                        row={row}
                        onOpen={() => openConversation(row.id)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </PageLayout>

      <ContactDrawer
        selected={selected_conversation}
        open={drawerOpen}
        onClose={closeDrawer}
        onSelect={openConversation}
      />
    </AppLayout>
  )
}
